import { CdkFixedSizeVirtualScroll, CdkVirtualForOf, CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  forwardRef,
  inject,
  Input,
  model,
  OnInit,
  output,
  Renderer2,
  signal,
  viewChild,
  ViewChild,
  viewChildren,
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { DataStoreStatus } from '@compiere-ws/models/compiere-data-json';
import PrimeOverlayComponent from '@iupics-components/overrided/prime-overlay/prime-overlay.component';
import EditTabUiComponent from '@iupics-components/standard/layouts/edit-tab-ui/edit-tab-ui.component';
import ValuePreferencePanelComponent from '@iupics-components/standard/value-preference-panel/value-preference-panel.component';
import { AbstractDataContainer, AbstractDataContainerCallout } from '@iupics-manager/models/abstract-datacontainer';
import { IupicsDataField } from '@iupics-manager/models/iupics-data';
import { DropdownComponent } from '@iupics-util/components/dropdown/dropdown.component';
import { TranslateModule } from '@ngx-translate/core';
import { IconEntry, IconsManagerService } from '@web-desktop/controllers/icons-manager.service';
import { TooltipOptions } from 'primeng/api';
import { OverlayPanel } from 'primeng/overlaypanel';
import { TooltipModule } from 'primeng/tooltip';
import { forkJoin, map, merge, shareReplay, tap } from 'rxjs';
import { LabelUiComponent } from '../label-ui/label-ui.component';
import { InputIconHighlightedDirective } from './input-icon-highlight.directive';

@Component({
  selector: 'iu-input-icon-ui',
  standalone: true,
  imports: [
    TranslateModule,
    TooltipModule,
    NgClass,
    NgTemplateOutlet,
    FormsModule,
    PrimeOverlayComponent,
    forwardRef(() => ValuePreferencePanelComponent),
    DropdownComponent,
    CdkVirtualScrollViewport,
    CdkFixedSizeVirtualScroll,
    CdkVirtualForOf,
    InputIconHighlightedDirective,
    LabelUiComponent,
  ],
  templateUrl: './input-icon-ui.component.html',
  styleUrl: './input-icon-ui.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InputIconUiComponent extends AbstractDataContainer implements OnInit, AfterViewInit {
  readonly TOOLTIP_OPTIONS: TooltipOptions = {
    tooltipPosition: 'top',
    tooltipZIndex: '2100',
    escape: false,
    showDelay: 500,
    hideDelay: 250,
    life: 3000,
  };

  readonly #renderer = inject(Renderer2);
  readonly #iconsManagerService = inject(IconsManagerService);

  @Input()
  columnName: string;
  @Input()
  placeHolder: string;
  @Input()
  data: IupicsDataField;
  @Input()
  isKeyUpTriggerable = false;

  readonly rawValue = model('');

  @Input()
  set fieldValue(value: string) {
    this.rawValue.set(value);
  }
  get fieldValue(): string {
    return this.rawValue();
  }

  @ViewChild('input', { static: true })
  inputRef: ElementRef<HTMLInputElement>;
  @ViewChild('opConflict', { static: true })
  opConflict: OverlayPanel;

  readonly virtualScroll = viewChild(CdkVirtualScrollViewport);
  readonly fakeIconItem = viewChild<ElementRef>('fake');
  readonly highlightElements = viewChildren(InputIconHighlightedDirective);
  readonly categoryElements = viewChildren<ElementRef>('button');

  readonly #icons$ = forkJoin({
    fawes: this.#iconsManagerService.getFontAwesomeIcons(),
    icomoon: this.#iconsManagerService.getIcomoonIcons(),
  }).pipe(
    map(({ fawes, icomoon }) => ({ ...fawes, icomoon })),
    shareReplay()
  );

  readonly #icons = toSignal(this.#icons$);
  readonly selectedCategory = signal<undefined | string>(undefined);
  readonly open = signal(false);
  readonly iconItemSize = signal(0);

  // keyboard nav
  readonly isKeyboardNav = signal(false);
  onMouseMove: () => void = undefined;
  readonly highlightIndex = signal<number>(-2);
  readonly highlightedCategoryEl = signal<HTMLElement | undefined>(undefined);

  readonly value = toSignal(
    merge(this.#icons$.pipe(map(() => this.fieldValue)), toObservable(this.rawValue)).pipe(
      map((v) => this.searchIcon(v))
    )
  );

  readonly categories = computed(() => (this.#icons() ? Object.keys(this.#icons()) : []));
  readonly icons = computed(() => {
    if (!this.#icons()) {
      return [];
    }
    let icons: IconEntry[];
    if (this.selectedCategory()) {
      icons = this.#icons()?.[this.selectedCategory()] ?? [];
    } else {
      icons = Object.keys(this.#icons()).reduce((acc, category) => [...acc, ...this.#icons()[category]], []);
    }

    if (!this.rawValue() || this.rawValue()?.trim() === '') {
      return icons;
    }

    return icons.filter(({ id, displayValue, searchTerms }) =>
      [id, displayValue, ...searchTerms].some((st) => st.includes(this.rawValue().toLowerCase()))
    );
  });

  dataContainers: AbstractDataContainerCallout;

  // outputs
  @Input()
  readonly fieldValueChange = output<IconEntry | undefined>();
  readonly enterKey = output<Event>();

  readonly resizeObs = new ResizeObserver((entries) => {
    const height = entries[0]?.target.getBoundingClientRect().height;
    if (height && height > 0) {
      this.iconItemSize.set(height);
    }
  });

  get highlightedEl(): HTMLElement | undefined {
    if (this.#isCategoriesHighlighted()) {
      return undefined;
    }
    return this.virtualScroll().elementRef.nativeElement.querySelector<HTMLElement>('[data-highlighted="true"]');
  }

  constructor() {
    super();

    // Handle item size
    toObservable(this.fakeIconItem)
      .pipe(
        tap((elRef) => this.resizeObs.observe(elRef.nativeElement)),
        takeUntilDestroyed()
      )
      .subscribe();

    inject(DestroyRef).onDestroy(() => {
      if (this.resizeObs) {
        this.resizeObs.disconnect();
      }
    });
  }

  ngOnInit(): void {
    super.ngOnInit();
    if (this.cssClass !== undefined) {
      this.cssGrid = this.cssClass;
    }
    this.cssClass = ' ' + this.cssGrid;
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    let parentComp = this.DOMParentComponent;
    while (parentComp && !(parentComp instanceof EditTabUiComponent)) {
      parentComp = parentComp.DOMParentComponent;
    }
    if (parentComp instanceof EditTabUiComponent) {
      this.dataContainers = new AbstractDataContainerCallout(parentComp.dataContainers);
    }
    this.checkFocus();
  }

  checkFocus(): void {
    if (
      this.data?.IsDefaultFocus &&
      !this.isReadOnly &&
      this.editViewParent?.editTabs?.[0]?.dataStored?.status === DataStoreStatus.NEWRECORD &&
      (this.fieldValue === null || this.fieldValue === undefined)
    ) {
      this.inputRef.nativeElement.focus();
    }
  }

  showConflictPanel(ev: MouseEvent): void {
    this.opConflict.toggle(ev);
  }

  //#region Events
  onUserInput(event: InputEvent): void {
    if (
      this.data?.vFormat &&
      event.inputType === 'insertText' &&
      (event.target as HTMLInputElement).value.length > this.data.vFormat.length
    ) {
      (event.target as HTMLInputElement).value = (event.target as HTMLInputElement).value.slice(
        0,
        this.data.vFormat.length
      );
    }
  }

  onKeyDown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'Tab':
        this.toggle(false);
        break;

      case 'Enter':
        if (this.highlightedCategoryEl()) {
          this.highlightedCategoryEl().click();
        } else if (this.highlightedEl) {
          this.highlightedEl.click();
        } else {
          this.enterKey.emit(event);
        }
        break;

      case 'ArrowDown':
      case 'ArrowUp':
        event.preventDefault();
        this.#setHighlightEl(event.key);
        break;

      case 'ArrowRight':
      case 'ArrowLeft':
        if (this.#isCategoriesHighlighted()) {
          event.preventDefault();
          this.#setHighlightCategoryEl(event.key);
        }
        break;

      default:
        this.virtualScroll()?.scrollToIndex(0);
    }
  }

  onKeyUp(event: KeyboardEvent): void {
    if (this.isStandalone && this.isKeyUpTriggerable) {
      this.fieldValueChange.emit(this.value());
    }
  }

  onClick(event: MouseEvent, input: HTMLElement): void {
    if (event.button > 0) {
      return;
    }
    input.focus();
  }
  //#endregion

  #activateKeyboardNavigation(): void {
    if (this.isKeyboardNav()) {
      return;
    }
    this.isKeyboardNav.set(true);
    this.onMouseMove = this.#renderer.listen('document', 'mousemove', () => this.#disableKeyboardNavigation());
  }

  #disableKeyboardNavigation(): void {
    if (!this.isKeyboardNav()) {
      return;
    }
    this.isKeyboardNav.set(false);
    this.onMouseMove();
    this.onMouseMove = undefined;
  }

  #setHighlightEl(_step: 'ArrowDown' | 'ArrowUp' | undefined): void {
    this.#activateKeyboardNavigation();
    if (!_step) {
      this.highlightIndex.set(-2);
      return;
    }

    const step = _step === 'ArrowUp' ? -1 : 1;
    let index = this.highlightIndex();

    if (index === -2) {
      index = _step === 'ArrowDown' ? -1 : this.icons()?.length - 1;
    } else if (index === -1) {
      index += step;
      if (index === -2) {
        index = this.icons().length - 1;
      }
    } else {
      index += step;

      if (index >= this.icons().length) {
        index = -1;
      }
      if (index < -2) {
        index = this.icons().length - 1;
      }
    }

    this.highlightIndex.set(index);
    if (this.#isCategoriesHighlighted(index)) {
      this.#setHighlightCategoryEl('ArrowRight');
    } else {
      this.#setHighlightCategoryEl(undefined);
      this.virtualScroll().scrollToIndex(index);
    }
  }

  #isCategoriesHighlighted(index: number = this.highlightIndex()): boolean {
    return index === -1;
  }

  #setHighlightCategoryEl(_step: 'ArrowLeft' | 'ArrowRight' | undefined): void {
    if (!_step) {
      this.highlightedCategoryEl.set(undefined);
      return;
    }

    const step = _step === 'ArrowLeft' ? -1 : 1;
    let index = 0;
    if (this.highlightedCategoryEl()) {
      index = this.categoryElements().findIndex((el) => el.nativeElement === this.highlightedCategoryEl());
      // apply step
      if (index === 0 && step === -1) {
        index = this.categoryElements().length - 1;
      } else if (index < 0 || (index === this.categoryElements().length - 1 && step === 1)) {
        index = 0;
      } else {
        index += step;
      }
    }

    this.highlightedCategoryEl.set(this.categoryElements()?.[index].nativeElement);
  }

  toggle(value: boolean): void {
    this.open.set(value);
    if (!value) {
      this.inputRef.nativeElement?.blur();
    } else {
      this.resetHighlight();
      setTimeout(() => this.virtualScroll()?.scrollToIndex(0), 500);
    }
  }

  resetHighlight(): void {
    this.#setHighlightEl(undefined);
    this.#setHighlightCategoryEl(undefined);
  }

  updateLastItemHover(index: number): void {
    if (!this.isKeyboardNav()) {
      this.highlightIndex.set(index);
    }
  }

  selectCategory(event: MouseEvent, category: string | undefined, input: HTMLElement): void {
    if (event) {
      event.stopPropagation();
    }
    this.selectedCategory.set(category);
    if (input) {
      input.focus();
    }
  }

  select(event: MouseEvent, icon: IconEntry): void {
    event.stopPropagation();
    this.fieldValue = icon.id;
    const value = this.value() ??
      this.searchIcon(icon.id) ?? { id: this.rawValue(), displayValue: this.rawValue(), searchTerms: [] };
    this.fieldValueChange.emit(value);
    this.dataChange(value.id);
    this.toggle(false);
  }

  private searchIcon(id: string): IconEntry | undefined {
    if (!this.#icons()) {
      return undefined;
    }
    const values = Object.values(this.#icons()).flat();
    if (values.length === 1) {
      return values[0];
    }
    return values.find((i) => i.id === id);
  }

  trackByFn(index: number, item: IconEntry): string {
    return `${index}-${item.id}`;
  }
}
